import React, { useRef } from 'react';
import { motion, useMotionValue, useTransform, useMotionTemplate } from 'motion/react';
import { Globe2, Leaf, ShieldAlert, Award, Compass, ArrowRight } from 'lucide-react';

export default function TransicionSaneamiento() {
  const globeRef = useRef<HTMLDivElement>(null);
  
  // Parallax interactivo para la columna del globo
  const x = useMotionValue(0);
  const y = useMotionValue(0);
  const rotateX = useTransform(y, [-300, 300], [15, -15]);
  const rotateY = useTransform(x, [-300, 300], [-15, 15]);

  function handleMouseMove(event: React.MouseEvent<HTMLDivElement>) {
    const rect = globeRef.current?.getBoundingClientRect();
    if (rect) {
      const centerX = rect.left + rect.width / 2;
      const centerY = rect.top + rect.height / 2;
      x.set(event.clientX - centerX);
      y.set(event.clientY - centerY);
    }
  }

  function handleMouseLeave() {
    x.set(0);
    y.set(0);
  }

  // Detectar idioma
  const isEn = typeof document !== 'undefined' && document.documentElement.lang === 'en';

  return (
    <section className="py-24 bg-[#050B14] relative overflow-hidden border-b border-white/5">
      {/* Luces de fondo premium */}
      <div className="absolute top-1/2 left-0 w-[500px] h-[500px] bg-emerald-500/5 blur-[150px] rounded-full pointer-events-none" />
      <div className="absolute bottom-0 right-0 w-[600px] h-[600px] bg-cyan-500/5 blur-[180px] rounded-full pointer-events-none" />

      <div className="max-w-[1440px] w-full mx-auto px-6 md:px-12 lg:px-20 relative z-10">
        <div className="grid lg:grid-cols-12 gap-16 items-center">
          
          {/* COLUMNA IZQUIERDA: GLOBO 3D INTERACTIVO */}
          <div className="lg:col-span-5 flex justify-center items-center relative overflow-visible w-full">
            <motion.div
              ref={globeRef}
              onMouseMove={handleMouseMove}
              onMouseLeave={handleMouseLeave}
              style={{ rotateX, rotateY, transformStyle: "preserve-3d" }}
              className="relative w-[285px] h-[285px] sm:w-[320px] sm:h-[320px] md:w-[400px] md:h-[400px] flex items-center justify-center cursor-grab active:cursor-grabbing group mx-auto"
            >
              {/* Brillo de fondo del Globo */}
              <div className="absolute w-[200px] h-[200px] sm:w-[240px] sm:h-[240px] bg-gradient-to-tr from-emerald-500/10 to-cyan-500/10 rounded-full blur-[40px] group-hover:scale-110 transition-transform duration-700" />

              {/* Globo Central SVG */}
              <div className="relative w-[170px] h-[170px] sm:w-[200px] sm:h-[200px] md:w-[260px] md:h-[260px] rounded-full border border-emerald-500/20 flex items-center justify-center bg-[#071324]/40 backdrop-blur-md shadow-[0_0_50px_rgba(16,185,129,0.15)]" style={{ transform: "translateZ(30px)" }}>
                <Globe2 className="w-16 h-16 sm:w-24 sm:h-24 md:w-32 md:h-32 text-emerald-400 opacity-80 group-hover:scale-105 group-hover:text-emerald-300 transition-all duration-700" />
                
                {/* Cuadrícula interna del globo */}
                <div className="absolute inset-0 rounded-full border border-dashed border-cyan-500/10 m-2 animate-[spin_40s_linear_infinite]" />
                <div className="absolute inset-0 rounded-full border border-dashed border-emerald-500/10 m-6 animate-[spin_20s_linear_infinite_reverse]" />
              </div>

              {/* Anillo de Órbita 1 */}
              <motion.div
                animate={{ rotate: 360 }}
                transition={{ duration: 15, repeat: Infinity, ease: "linear" }}
                className="absolute w-[240px] h-[240px] sm:w-[280px] sm:h-[280px] md:w-[350px] md:h-[350px] rounded-full border border-emerald-500/20 border-t-transparent border-b-transparent pointer-events-none"
                style={{ transform: "rotateX(70deg) rotateY(15deg)", transformStyle: "preserve-3d" }}
              />

              {/* Anillo de Órbita 2 */}
              <motion.div
                animate={{ rotate: -360 }}
                transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
                className="absolute w-[260px] h-[260px] sm:w-[300px] sm:h-[300px] md:w-[380px] md:h-[380px] rounded-full border border-cyan-500/15 border-l-transparent border-r-transparent pointer-events-none"
                style={{ transform: "rotateX(30deg) rotateY(-45deg)", transformStyle: "preserve-3d" }}
              />

              {/* ETIQUETAS FLOTANTES (PARALLAX 3D) */}
              
              {/* Etiqueta 1: Hot Tapping Mínimas Emisiones (Arriba derecha) */}
              <motion.div
                animate={{ y: [0, -10, 0] }}
                transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
                className="absolute -top-3 -right-2 sm:-top-4 sm:-right-4 md:right-0 bg-[#071324]/95 backdrop-blur-md border border-emerald-500/40 px-3.5 py-2 sm:px-4.5 sm:py-3 rounded-xl shadow-[0_10px_30px_rgba(16,185,129,0.2)] flex items-center gap-2 sm:gap-2.5"
                style={{ transform: "translateZ(80px)" }}
              >
                <div className="w-2 h-2 sm:w-3 sm:h-3 rounded-full bg-emerald-400 animate-pulse shadow-[0_0_8px_#10b981]" />
                <div>
                  <div className="text-[10px] sm:text-[11px] text-emerald-300 font-bold uppercase tracking-widest leading-none mb-1">Hot Tapping</div>
                  <div className="text-[11px] sm:text-[13px] text-white font-black leading-none">{isEn ? "Minimal Emissions" : "Mínimas Emisiones"}</div>
                </div>
              </motion.div>

              {/* Etiqueta 2: Impacto Positivo Restauración (Abajo izquierda) */}
              <motion.div
                animate={{ y: [0, 10, 0] }}
                transition={{ duration: 4, repeat: Infinity, ease: "easeInOut", delay: 2 }}
                className="absolute -bottom-3 -left-2 sm:-bottom-4 sm:-left-4 md:left-0 bg-[#071324]/95 backdrop-blur-md border border-cyan-500/40 px-3.5 py-2 sm:px-4.5 sm:py-3 rounded-xl shadow-[0_10px_30px_rgba(6,182,212,0.2)] flex items-center gap-2 sm:gap-2.5"
                style={{ transform: "translateZ(60px)" }}
              >
                <div className="w-2 h-2 sm:w-3 sm:h-3 rounded-full bg-cyan-400 animate-pulse shadow-[0_0_8px_#06b6d4]" />
                <div>
                  <div className="text-[10px] sm:text-[11px] text-cyan-300 font-bold uppercase tracking-widest leading-none mb-1">{isEn ? "Positive Impact" : "Impacto Positivo"}</div>
                  <div className="text-[11px] sm:text-[13px] text-white font-black leading-none">{isEn ? "Ecological Restoration" : "Restauración Ecológica"}</div>
                </div>
              </motion.div>

            </motion.div>
          </div>

          {/* COLUMNA DERECHA: TÍTULO, MENSAJE Y REJILLA METRICAS 2X2 */}
          <div className="lg:col-span-7 flex flex-col justify-center">
            
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="mb-10 text-center lg:text-left"
            >
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-emerald-500/30 bg-emerald-500/10 text-white font-semibold text-xs mb-6 uppercase tracking-widest backdrop-blur-md">
                <Leaf className="w-4 h-4 text-emerald-400 animate-pulse" />
                <span>{isEn ? "Sustainable Engineering" : "Ingeniería Sustentable"}</span>
              </div>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-black text-white mb-6 leading-tight tracking-tight">
                {isEn ? (
                  <>Operations Aligned with the <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 to-cyan-400">Energy Transition</span></>
                ) : (
                  <>Operaciones Alineadas con la <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 to-cyan-400">Transición Energética</span></>
                )}
              </h2>
              <p className="text-gray-400 text-base md:text-lg font-light leading-relaxed max-w-2xl">
                {isEn 
                  ? "We position ourselves at the forefront of sustainability and environmental care. We reduce ecological impact through high-precision cutting methodologies, venting mitigation, and remediation of impacted soils."
                  : "Nos posicionamos a la vanguardia de la sustentabilidad y el cuidado ambiental. Reducimos el impacto ecológico mediante metodologías de corte de alta precisión, mitigación de venteo y saneamiento de suelos impactados."
                }
              </p>
            </motion.div>

            {/* Rejilla de Métricas 2x2 */}
            <div className="grid sm:grid-cols-2 gap-6">
              
              {/* Tarjeta 1: Mitigación de Venteo */}
              <motion.div
                whileHover={{ scale: 1.03, translateY: -4 }}
                transition={{ duration: 0.2 }}
                className="p-6 rounded-2xl bg-emerald-950/10 border border-emerald-500/20 hover:border-emerald-400/40 hover:bg-emerald-950/20 hover:shadow-[0_0_30px_rgba(16,185,129,0.1)] transition-all group cursor-default"
              >
                <div className="w-12 h-12 rounded-xl bg-emerald-950/60 border border-emerald-500/30 flex items-center justify-center text-emerald-400 mb-5 group-hover:text-white group-hover:bg-emerald-500 transition-colors">
                  <Compass className="w-6 h-6" />
                </div>
                <h3 className="text-lg font-bold text-white mb-2 group-hover:text-emerald-400 transition-colors">
                  {isEn ? "Venting & Flaring Mitigation" : "Mitigación de Venteo y Quema"}
                </h3>
                <p className="text-gray-400 text-sm leading-relaxed">
                  {isEn 
                    ? "We minimize flaring or venting by ensuring maximum tightness in our high-resistance and precision seals."
                    : "Minimizamos la quema o venteo al garantizar la máxima hermeticidad en nuestros sellos de alta resistencia y precisión."
                  }
                </p>
              </motion.div>

              {/* Tarjeta 2: Prevención Derrames */}
              <motion.div
                whileHover={{ scale: 1.03, translateY: -4 }}
                transition={{ duration: 0.2 }}
                className="p-6 rounded-2xl bg-cyan-950/10 border border-cyan-500/20 hover:border-cyan-400/40 hover:bg-cyan-950/20 hover:shadow-[0_0_30px_rgba(6,182,212,0.1)] transition-all group cursor-default"
              >
                <div className="w-12 h-12 rounded-xl bg-cyan-950/60 border border-cyan-500/30 flex items-center justify-center text-cyan-400 mb-5 group-hover:text-white group-hover:bg-cyan-500 transition-colors">
                  <ShieldAlert className="w-6 h-6" />
                </div>
                <h3 className="text-lg font-bold text-white mb-2 group-hover:text-cyan-400 transition-colors">
                  {isEn ? "Spill Prevention" : "Prevención de Derrames"}
                </h3>
                <p className="text-gray-400 text-sm leading-relaxed">
                  {isEn 
                    ? "Hermetic containment systems and redundant mechanical sealing to safeguard the surrounding ecosystems."
                    : "Sistemas de contención hermética y sellado mecánico redundante para resguardar los ecosistemas circundantes."
                  }
                </p>
              </motion.div>

              {/* Tarjeta 3: HazMat III */}
              <motion.div
                whileHover={{ scale: 1.03, translateY: -4 }}
                transition={{ duration: 0.2 }}
                className="p-6 rounded-2xl bg-amber-950/10 border border-amber-500/20 hover:border-amber-400/40 hover:bg-amber-950/20 hover:shadow-[0_0_30px_rgba(245,158,11,0.1)] transition-all group cursor-default"
              >
                <div className="w-12 h-12 rounded-xl bg-amber-950/60 border border-amber-500/30 flex items-center justify-center text-amber-400 mb-5 group-hover:text-white group-hover:bg-amber-500 transition-colors">
                  <Award className="w-6 h-6" />
                </div>
                <h3 className="text-lg font-bold text-white mb-2 group-hover:text-amber-400 transition-colors">
                  {isEn ? "HazMat III Certification" : "Certificación HazMat III"}
                </h3>
                <p className="text-gray-400 text-sm leading-relaxed">
                  {isEn 
                    ? "Internationally certified technical personnel for the safe handling of hazardous materials and harmful substances."
                    : "Personal técnico certificado internacionalmente para el manejo seguro de materiales peligrosos y sustancias nocivas."
                  }
                </p>
              </motion.div>

              {/* Tarjeta 4: +15 Proyectos */}
              <motion.div
                whileHover={{ scale: 1.03, translateY: -4 }}
                transition={{ duration: 0.2 }}
                className="p-6 rounded-2xl bg-emerald-950/10 border border-emerald-500/20 hover:border-emerald-400/40 hover:bg-emerald-950/20 hover:shadow-[0_0_30px_rgba(16,185,129,0.1)] transition-all group cursor-default"
              >
                <div className="w-12 h-12 rounded-xl bg-emerald-950/60 border border-emerald-500/30 flex items-center justify-center text-emerald-400 mb-5 group-hover:text-white group-hover:bg-emerald-500 transition-colors">
                  <Leaf className="w-6 h-6" />
                </div>
                <h3 className="text-lg font-bold text-white mb-2 group-hover:text-emerald-400 transition-colors">
                  {isEn ? "+15 Remediation Projects" : "+15 Proyectos de Saneamiento"}
                </h3>
                <p className="text-gray-400 text-sm leading-relaxed">
                  {isEn 
                    ? "Comprehensive restoration of impacted areas and ecological recovery in oil and gas facilities."
                    : "Restauración integral de áreas impactadas y recuperación ecológica en instalaciones petroleras y gasíferas."
                  }
                </p>
              </motion.div>

            </div>

          </div>

        </div>
      </div>
    </section>
  );
}
